import { Flex } from '../UI/Flex/Flex';

import {
    useClientSettingStore,
    useStore,
    useStoreUI
} from '@assets/model/Store';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { DataAccessUtils } from '../../model/DataAccessUtils';
import { DealProcessing } from './DealProcessing/DealProcessing';
import * as style from './DealSearchContent.module.less';
import { DealSearchHeader } from './DealSearchHeader/DealSearchHeader';
import { DealThumbnail } from './DealThumbnail/DealThumbnail';
import { NoResultsFound } from './NoResultsFound/NoResultsFound';

import { DealMissingPassword } from './DealMissingPassword/DealMissingPassword';
import { DealSearchVirtualized } from './DealScrollVirtualized';

interface DealSearchContentProps {
    onDealSelect?: (dealId: string) => void;
}

export const DealSearchContent = observer((props: DealSearchContentProps) => {
    const store = useStore();
    const uiStore = useStoreUI();

    useEffect(() => {
        uiStore.dealPageIndustrySearch = [];
        uiStore.dealPageNameSearch = '';
    }, []);

    var dealKeys = Object.keys(store.reportDataDB);

    const filter_settings = useClientSettingStore().filterCompaniesForDemo;
    if (useClientSettingStore().isDemoFilterOn && filter_settings != null) {
        if (Object.keys(filter_settings).length > 0) {
            dealKeys = dealKeys.filter(
                dealId => filter_settings[dealId] === true
            );
        }
    }

    //Show completed reports
    var filtered = dealKeys.filter(deal => {
        var e = store.reportDataDB[deal];
        var status = e.status;
        if (status != 'completed') {
            return false;
        }

        var dataAccess = store.createDataAccess(deal);

        const companyName = dataAccess.companyName;
        const matchCompanyName =
            companyName
                .toLowerCase()
                .includes(uiStore.dealPageNameSearch.toLowerCase()) ||
            uiStore.dealPageNameSearch == '';

        const filter = uiStore.dealPageIndustrySearch;
        var industry = dataAccess.value('industry');
        var sub_industry = dataAccess.value('industrySubType');

        var matchIndustry = false;

        if (filter.length == 0) {
            matchIndustry = true;
        } else {
            filter.forEach(filterItem => {
                if (filterItem.type == 'category') {
                    if (filterItem.value == industry) {
                        matchIndustry = true;
                    }
                } else if (filterItem.type == 'sub_category') {
                    if (filterItem.value == sub_industry) {
                        matchIndustry = true;
                    }
                }
            });
        }

        return matchCompanyName && matchIndustry;
    });

    //sort by creation than name
    filtered = filtered.sort((a, b) => {
        const ep1 = store.reportDataDB[a].report_creation_epoch || 0;
        const ep2 = store.reportDataDB[b].report_creation_epoch || 0;

        if (ep1 === ep2) {
            const aName = store.reportDataDB[a].company_name;
            const bName = store.reportDataDB[b].company_name;
            return aName.localeCompare(bName);
        } else {
            // Apply sort order based on UI selection
            if (uiStore.dealPageSortOrder === 'oldest') {
                return ep1 - ep2; // Oldest first
            } else {
                return ep2 - ep1; // Newest first (default)
            }
        }
    });

    //Add pending jobs to view
    const pendingJobs = [];
    if (uiStore.dealPageNameSearch.length == 0) {
        for (const key in store.jobs) {
            var e = store.jobs[key];
            if (e.status == 'pending') {
                pendingJobs.push(key);
            }
        }
    }

    const queuedFilesInJobs = [];
    for (var jobId in store.jobs) {
        var job = store.jobs[jobId];

        if (job.status == 'pending') {
            queuedFilesInJobs.push(job.fileName.toLowerCase());
        }
    }

    var activeJob = store.activeJob;
    if (store.activeJob) {
        const job = store.activeJob;
        if (job.status == 'processing') {
            const fileName = activeJob.fileName.toLowerCase();
            queuedFilesInJobs.push(fileName);
        }
    }

    const pdfLocked = [];

    //Switch to sorting the view by the final IDs

    for (const key in store.reportDataDB) {
        var e = store.reportDataDB[key];
        var file = e['pdf'];
        if (
            e.status == 'failed' &&
            e['error'] == 'pdf_password' &&
            !queuedFilesInJobs.includes(file.toLowerCase())
        ) {
            pdfLocked.push(key);
        }
    }

    if (pendingJobs.length > 0) {
        pendingJobs.forEach((jobId: any, index) => {
            if (!filtered.includes(jobId)) {
                if (index < 4) {
                    filtered.unshift(jobId);
                } else {
                    filtered.push(jobId);
                }
            }
        });
    }

    if (pdfLocked.length > 0) {
        pdfLocked.forEach((jobId: any) => {
            if (!filtered.includes(jobId)) {
                filtered.unshift(jobId);
            }
        });
    }

    if (
        store.activeJob &&
        store.activeJob.status === 'processing' &&
        !filtered.includes(store.activeJob.jobId)
    ) {
        const jobs = store.jobs;
        for (const key in jobs) {
        }
        filtered.unshift(store.activeJob.jobId);
    }

    let deals = filtered.map(deal => {
        if (pdfLocked.includes(deal) && !pendingJobs.includes(deal)) {
            return <DealMissingPassword key={deal} jobId={deal} />;
        } else if (
            (store.activeJob &&
                store.activeJob.status === 'processing' &&
                deal === store.activeJob.jobId) ||
            pendingJobs.includes(deal)
        ) {
            return <DealProcessing key={deal} jobId={deal} />;
        }

        return <DealThumbnail name={deal} key={deal} />;
    });

    // Check if we should show "No Results Found"
    const hasActiveFilters =
        uiStore.dealPageNameSearch.length > 0 ||
        uiStore.dealPageIndustrySearch.length > 0;

    const shouldShowNoResults =
        hasActiveFilters &&
        filtered.length === 0 &&
        pendingJobs.length === 0 &&
        pdfLocked.length === 0 &&
        (!store.activeJob || store.activeJob.status !== 'processing');

    return (
        <>
            <Flex
                direction="column"
                className={style.container}
                justify="start"
            >
                <DealSearchHeader />
                {shouldShowNoResults ? (
                    <NoResultsFound />
                ) : (
                    <DealSearchVirtualized deals={deals} />
                )}
            </Flex>
        </>
    );
});
